import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { Scissors, Plus, ArrowUp, ArrowDown, Trash2 } from 'lucide-react';
import { AppShell, useShell } from '../components/AppShell';
import './Services.css';

const ServicesInner = () => {
  const { profile } = useShell();
  const shopId = profile?.shop_id;
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    duration_min: '',
    active: true,
    deposit_mode: 'inherit',
    deposit_type: 'fixed',
    deposit_amount: '',
    allow_pay_in_full: false,
  });

  useEffect(() => {
    if (shopId) fetchServices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shopId]);

  const fetchServices = async () => {
    if (!shopId) return;
    try {
      const { data, error } = await supabase
        .from('services')
        .select('*')
        .eq('shop_id', shopId)
        .order('sort_order', { ascending: true });

      if (error) throw error;
      setServices(data || []);
    } catch (error) {
      console.error('Error fetching services:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMove = async (index, direction) => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= services.length) return;

    const items = Array.from(services);
    const [movedItem] = items.splice(index, 1);
    items.splice(newIndex, 0, movedItem);

    // Update sort_order
    const updates = items.map((item, idx) => ({
      id: item.id,
      sort_order: idx + 1
    }));

    setServices(items);

    try {
      for (const update of updates) {
        await supabase
          .from('services')
          .update({ sort_order: update.sort_order })
          .eq('id', update.id);
      }
    } catch (error) {
      console.error('Error updating order:', error);
      fetchServices();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const isCustom = formData.deposit_mode === 'custom';
      const serviceData = {
        ...formData,
        shop_id: shopId,
        price: parseFloat(formData.price),
        duration_min: parseInt(formData.duration_min),
        sort_order: editingService ? editingService.sort_order : services.length + 1,
        deposit_mode: formData.deposit_mode,
        deposit_type: isCustom ? (formData.deposit_type || 'fixed') : null,
        deposit_amount: isCustom ? Number(formData.deposit_amount || 0) : null,
        allow_pay_in_full: !!formData.allow_pay_in_full,
      };

      if (editingService) {
        const { error } = await supabase
          .from('services')
          .update(serviceData)
          .eq('id', editingService.id);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('services')
          .insert([serviceData]);

        if (error) throw error;
      }

      setShowForm(false);
      setEditingService(null);
      setFormData({ name: '', description: '', price: '', duration_min: '', active: true, deposit_mode: 'inherit', deposit_type: 'fixed', deposit_amount: '', allow_pay_in_full: false });
      fetchServices();
    } catch (error) {
      console.error('Error saving service:', error);
      alert('Error saving service: ' + error.message);
    }
  };

  const handleEdit = (service) => {
    setEditingService(service);
    setFormData({
      name: service.name,
      description: service.description || '',
      price: service.price.toString(),
      duration_min: service.duration_min.toString(),
      active: service.active,
      deposit_mode: service.deposit_mode || 'inherit',
      deposit_type: service.deposit_type || 'fixed',
      deposit_amount: service.deposit_amount != null ? String(service.deposit_amount) : '',
      allow_pay_in_full: !!service.allow_pay_in_full,
    });
    setShowForm(true);
  };

  const handleToggleActive = async (service) => {
    try {
      const { error } = await supabase
        .from('services')
        .update({ active: !service.active })
        .eq('id', service.id);

      if (error) throw error;
      fetchServices();
    } catch (error) {
      console.error('Error toggling service:', error);
    }
  };

  const handleDelete = async (service) => {
    const confirmed = window.confirm(
      `Delete "${service.name}"?\n\nThis will also remove any past or upcoming bookings for this service. This cannot be undone.\n\nTip: if you just want to stop offering it, toggle it off instead.`
    );
    if (!confirmed) return;

    try {
      const { error } = await supabase
        .from('services')
        .delete()
        .eq('id', service.id);

      if (error) throw error;
      fetchServices();
    } catch (error) {
      console.error('Error deleting service:', error);
      alert('Error deleting service: ' + error.message);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingService(null);
    setFormData({ name: '', description: '', price: '', duration_min: '', active: true, deposit_mode: 'inherit', deposit_type: 'fixed', deposit_amount: '', allow_pay_in_full: false });
  };

  if (loading) {
    return (
      <main className="main">
        <div className="topbar"><div><h2>Services</h2></div></div>
        <div className="body"><div className="loading">Loading services...</div></div>
      </main>
    );
  }

  return (
    <main className="main">
        <div className="topbar">
          <div>
            <h2>Services</h2>
            <div className="sub">{services.length} active · drag to reorder on your booking page</div>
          </div>
          <button className="btn p" style={{ marginLeft: 'auto' }} onClick={() => setShowForm(true)}>
            <Plus size={16} /> Add service
          </button>
        </div>

        <div className="body">
          {showForm && (
            <div className="service-form-modal">
              <div className="service-form-content">
                <h3>{editingService ? 'Edit Service' : 'Add Service'}</h3>
                <form onSubmit={handleSubmit}>
                  <div className="field">
                    <label>Service Name</label>
                    <input
                      className="inp"
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      placeholder="e.g. Skin Fade"
                    />
                  </div>

                  <div className="field">
                    <label>Description</label>
                    <textarea
                      className="inp"
                      rows="2"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="e.g. Sharp fade, any length on top"
                    />
                  </div>

                  <div className="field-row">
                    <div className="field">
                      <label>Duration (minutes)</label>
                      <input
                        className="inp"
                        type="number"
                        value={formData.duration_min}
                        onChange={(e) => setFormData({ ...formData, duration_min: e.target.value })}
                        required
                        placeholder="45"
                        min="5"
                        step="5"
                      />
                    </div>

                    <div className="field">
                      <label>Price (£)</label>
                      <input
                        className="inp"
                        type="number"
                        step="0.01"
                        value={formData.price}
                        onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                        required
                        placeholder="24.00"
                        min="0"
                      />
                    </div>
                  </div>

                  <div className="field">
                    <label>Deposit for this service</label>
                    <div className="deposit-mode-picker" role="radiogroup" aria-label="Deposit mode">
                      <button
                        type="button"
                        role="radio"
                        aria-checked={formData.deposit_mode === 'inherit'}
                        className={`dm-btn ${formData.deposit_mode === 'inherit' ? 'on' : ''}`}
                        onClick={() => setFormData({ ...formData, deposit_mode: 'inherit' })}
                        data-testid="svc-dep-inherit"
                      >Use shop default</button>
                      <button
                        type="button"
                        role="radio"
                        aria-checked={formData.deposit_mode === 'none'}
                        className={`dm-btn ${formData.deposit_mode === 'none' ? 'on' : ''}`}
                        onClick={() => setFormData({ ...formData, deposit_mode: 'none' })}
                        data-testid="svc-dep-none"
                      >No deposit</button>
                      <button
                        type="button"
                        role="radio"
                        aria-checked={formData.deposit_mode === 'custom'}
                        className={`dm-btn ${formData.deposit_mode === 'custom' ? 'on' : ''}`}
                        onClick={() => setFormData({ ...formData, deposit_mode: 'custom' })}
                        data-testid="svc-dep-custom"
                      >Custom amount</button>
                    </div>

                    {formData.deposit_mode === 'custom' && (
                      <div className="svc-custom-deposit">
                        <div className="mode-seg" role="tablist" aria-label="Custom deposit type">
                          <button
                            type="button"
                            role="tab"
                            aria-selected={(formData.deposit_type || 'fixed') === 'fixed'}
                            className={`seg-btn ${(formData.deposit_type || 'fixed') === 'fixed' ? 'on' : ''}`}
                            onClick={() => setFormData({ ...formData, deposit_type: 'fixed' })}
                            data-testid="svc-dep-type-fixed"
                          >£ Fixed</button>
                          <button
                            type="button"
                            role="tab"
                            aria-selected={formData.deposit_type === 'percent'}
                            className={`seg-btn ${formData.deposit_type === 'percent' ? 'on' : ''}`}
                            onClick={() => setFormData({ ...formData, deposit_type: 'percent' })}
                            data-testid="svc-dep-type-percent"
                          >% of price</button>
                        </div>
                        <div className="svc-custom-input">
                          {(formData.deposit_type || 'fixed') === 'fixed' ? '£' : ''}
                          <input
                            className="inp"
                            type="number"
                            min="1"
                            step="1"
                            value={formData.deposit_amount}
                            onChange={(e) => setFormData({ ...formData, deposit_amount: e.target.value })}
                            placeholder={formData.deposit_type === 'percent' ? '20' : '5'}
                            data-testid="svc-dep-amount"
                          />
                          {formData.deposit_type === 'percent' ? '%' : ''}
                          {formData.deposit_type === 'percent'
                            && Number(formData.deposit_amount) > 0
                            && Number(formData.price) > 0 && (
                              <span className="live-conv" data-testid="svc-dep-preview">
                                = £{Math.ceil((Number(formData.price) * Number(formData.deposit_amount)) / 100)}
                              </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Prepay-in-full — only meaningful when a deposit
                      is required (else £0 vs full is the same choice
                      the customer already has). */}
                  <div className="form-group" data-testid="svc-pay-in-full-row">
                    <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={!!formData.allow_pay_in_full}
                        onChange={(e) => setFormData({ ...formData, allow_pay_in_full: e.target.checked })}
                        data-testid="svc-allow-pay-in-full"
                        style={{ marginTop: 3 }}
                      />
                      <span>
                        <b style={{ fontSize: 14 }}>Allow customers to pay in full</b>
                        <br />
                        <small style={{ color: 'var(--mute)', fontSize: 12, lineHeight: 1.4 }}>
                          Adds a &ldquo;Pay full &pound;{Number(formData.price || 0) || 'X'} now&rdquo; option next to the deposit at checkout. Great for pre-paid gift-card jobs or nervous first-time customers.
                          {' '}<em>Only surfaces to the customer when this service actually requires a deposit &mdash; a £0 deposit service is already effectively pre-paying nothing.</em>
                        </small>
                      </span>
                    </label>
                  </div>

                  <div className="form-actions">
                    <button type="button" className="btn s" onClick={handleCancel}>
                      Cancel
                    </button>
                    <button type="submit" className="btn p">
                      {editingService ? 'Save Changes' : 'Add Service'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          <div className="panel">
            {services.map((service, index) => (
              <div
                key={service.id}
                className={`svc-card ${!service.active ? 'inactive' : ''}`}
              >
                <div className="reorder-btns">
                  <button
                    className="move-btn"
                    onClick={() => handleMove(index, 'up')}
                    disabled={index === 0}
                  >
                    <ArrowUp size={14} />
                  </button>
                  <button
                    className="move-btn"
                    onClick={() => handleMove(index, 'down')}
                    disabled={index === services.length - 1}
                  >
                    <ArrowDown size={14} />
                  </button>
                </div>
                <span className="ic">
                  <Scissors size={18} />
                </span>
                <div className="info">
                  <b>{service.name}</b>
                  <small>{service.description || 'No description'}</small>
                  {/* Mobile-portrait-only combined meta: duration · description
                      on one truncated line. Hidden on desktop/landscape. */}
                  <small className="svc-meta-m">
                    <span className="mono">{service.duration_min}</span> min
                    {service.description ? ` · ${service.description}` : ''}
                  </small>
                </div>
                <span className="dur">{service.duration_min} min</span>
                <span className="price">£{service.price.toFixed(2)}</span>
                <label className="tog">
                  <input
                    type="checkbox"
                    checked={service.active}
                    onChange={() => handleToggleActive(service)}
                  />
                  <span className="tog-slider"></span>
                </label>
                <button
                  className="edit-btn"
                  onClick={() => handleEdit(service)}
                >
                  Edit
                </button>
                <button
                  className="delete-btn"
                  onClick={() => handleDelete(service)}
                  data-testid={`service-delete-${service.id}`}
                  aria-label={`Delete ${service.name}`}
                  title="Delete service"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}

            {services.length === 0 && (
              <div className="empty-state">
                <p>No services yet. Add your first service to get started.</p>
              </div>
            )}
          </div>
        </div>
      </main>
  );
};

const Services = () => (
  <AppShell>
    <ServicesInner />
  </AppShell>
);

export default Services;
